/**
 * Overnumbered prints are those whose collector number is past the printed
 * set size (`167/166`). The size lives on the set calendar, not in the name.
 *
 * Signed prints (`189s`, `299*`) are past the set size, so they match too.
 */
import { cardCollectorNumber, setCodeFromRiftboundId } from './cardCollectorNumber.js';
import { SET_RELEASE_CALENDAR, setCollectorTotal } from './setReleaseCalendar.js';

/** Leading collector digits. `189s` and `026a` are 189 and 26. */
export function overnumberedCollectorValue(card) {
  const token = cardCollectorNumber(card);
  const match = String(token).match(/^(\d+)/);
  return match ? Number(match[1]) : null;
}

export function isOvernumberedCard(card) {
  if (!card) return false;
  const setCode = card.setCode || card.set_code || setCodeFromRiftboundId(card.riftboundId || card.riftbound_id);
  const total = setCollectorTotal(setCode);
  if (!Number.isFinite(total)) return false;
  const collector = overnumberedCollectorValue(card);
  return collector != null && collector > total;
}

/**
 * SQL for the same check. Collector token matches `collectorNumberFromRiftboundId`:
 * the middle of a 3+ part id (`ogn-304-298`, `ven-195-star-166`), otherwise the
 * second part (`VEN-189s`).
 *
 * @param {{ setCodeExpr?: string, riftboundIdExpr?: string, numberExpr?: string }} [columns]
 */
export function overnumberedFilterSql({
  setCodeExpr = 'upper(set_code)',
  riftboundIdExpr = "COALESCE(riftbound_id, '')",
  numberExpr = "COALESCE(card_data->>'number', '')",
} = {}) {
  const arms = SET_RELEASE_CALENDAR
    .filter((entry) => Number.isFinite(entry.collectorTotal))
    .map((entry) => `WHEN '${String(entry.code).toUpperCase()}' THEN ${entry.collectorTotal}`)
    .join(' ');
  const parts = `string_to_array(${riftboundIdExpr}, '-')`;
  const partCount = `COALESCE(array_length(${parts}, 1), 0)`;
  const fromId = `CASE
    WHEN ${partCount} >= 3 THEN array_to_string((${parts})[2:${partCount} - 1], '-')
    WHEN ${partCount} = 2 THEN split_part(${riftboundIdExpr}, '-', 2)
    ELSE NULL
  END`;
  const token = `regexp_replace(
    regexp_replace(lower(COALESCE(${fromId}, NULLIF(${numberExpr}, ''))), '[*★]+$', 's'),
    '^([0-9]+)-star$',
    '\\1s'
  )`;
  return `(
    substring(${token} from '^([0-9]+)')::int
    > CASE ${setCodeExpr} ${arms} ELSE NULL END
  )`;
}
